import styled from "styled-components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useMemo, useRef } from "react";
import { Button, ButtonGroup, Form, Overlay, Popover } from "react-bootstrap";

import type { CommonAPI, RateMomentResponse } from "./types";
import { useToastManager } from "@bagel-web/components";

export interface EventStreamCache {
  event_wrappers: Array<{
    id: string;
    rating?: {
      is_positive: boolean;
      rationale: string | null;
    };
  }>;
}

const StyledPopover = styled(Popover)`
  max-width: none !important;
  width: auto;

  .popover-body {
    overflow: visible;
    padding: 0.5rem;
  }
`;

const StyledTextarea = styled(Form.Control)`
  width: 300px;
  min-width: 250px;
  resize: both;
  overflow: auto;
  margin-bottom: 0.5rem;
  display: block;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

interface RatingInput {
  sessionId: string;
  momentId: number;
  eventId: string;
  rating: {
    is_positive: boolean;
    rationale: string | null;
  };
}

interface DeleteRatingInput {
  eventId: string;
}

export const LLMEventRating = ({
  sessionId,
  agentId,
  momentId,
  eventId,
  isPositive,
  rationale,
  commonAPI,
}: {
  sessionId: string;
  agentId: string;
  momentId: number;
  eventId: string;
  isPositive?: boolean;
  rationale?: string | null;
  commonAPI: CommonAPI;
}) => {
  const [isPositiveRating, setIsPositiveRating] = useState<boolean | undefined>(
    isPositive
  );
  const [ratingRationale, setRatingRationale] = useState<
    string | null | undefined
  >(rationale);
  const [showPopover, setShowPopover] = useState<boolean>(false);

  const rationaleButtonRef = useRef<HTMLButtonElement>(null);
  const pendingCacheUpdateRef = useRef<RateMomentResponse | null>(null);

  const queryClient = useQueryClient();
  const { publishToast } = useToastManager();

  const updateCache = () => {
    // Update the event-stream cache with the new rating data after the popover closes
    // This is to prevent the popover from closing after saving since updating
    // the cache triggers a rerender, which causes the popover to close.
    if (pendingCacheUpdateRef.current) {
      const response = pendingCacheUpdateRef.current;
      pendingCacheUpdateRef.current = null;

      queryClient.setQueryData(
        ["event_streams", "get_detail", { sessionId, agentId, momentId }],
        (oldData: EventStreamCache | undefined) => {
          if (!oldData || !oldData.event_wrappers) return oldData;

          return {
            ...oldData,
            event_wrappers: oldData.event_wrappers.map((event) =>
              event.id === eventId
                ? {
                    ...event,
                    rating: {
                      is_positive: response.is_positive,
                      rationale: response.rationale,
                    },
                  }
                : event
            ),
          };
        }
      );
    }
  };

  useEffect(() => {
    setIsPositiveRating(isPositive);
    setRatingRationale(rationale);
  }, [momentId, eventId]);

  // Flush pending cache updates when navigating away
  useEffect(() => {
    return () => {
      updateCache();
    };
  }, [momentId, eventId, sessionId, agentId, queryClient]);

  const createOrUpdateRating = useMutation({
    mutationFn: async (ratingInput: RatingInput) =>
      await commonAPI.createOrUpdateRating(ratingInput),
    onSuccess: (response: RateMomentResponse) => {
      setIsPositiveRating(response.is_positive);
      setRatingRationale(response.rationale);
      publishToast(`Rating for moment ${momentId} saved`);
      setShowPopover(true);

      // Store the response to update cache later when popover closes
      pendingCacheUpdateRef.current = response;

      if (isPositiveRating !== response.is_positive) {
        setIsPositiveRating(response.is_positive);
        updateCache();
      }
    },
  });

  const deleteRating = useMutation({
    mutationFn: async (deleteRatingData: DeleteRatingInput) =>
      await commonAPI.deleteRating(deleteRatingData),
    onSuccess: () => {
      publishToast(`Rating for moment ${momentId} deleted`);
      // Update the cache to remove the rating
      queryClient.setQueryData(
        ["event_streams", "get_detail", { sessionId, agentId, momentId }],
        (oldData: EventStreamCache | undefined) => {
          if (!oldData || !oldData.event_wrappers) return oldData;

          return {
            ...oldData,
            event_wrappers: oldData.event_wrappers.map((event) =>
              event.id === eventId
                ? {
                    ...event,
                    rating: undefined,
                  }
                : event
            ),
          };
        }
      );
    },
  });

  const handleRating = (newRating: boolean) => () => {
    if (newRating != isPositiveRating) {
      createOrUpdateRating.mutate({
        sessionId,
        momentId,
        eventId,
        rating: {
          is_positive: newRating,
          rationale: ratingRationale || null,
        },
      });
    } else {
      // The user has clicked the same rating again to undo the rating
      setIsPositiveRating(undefined);
      deleteRating.mutate({
        eventId: eventId,
      });
    }
  };

  const handleSaveRationale = () => {
    createOrUpdateRating.mutate({
      sessionId,
      momentId,
      eventId,
      rating: {
        is_positive: isPositiveRating!,
        rationale: ratingRationale!,
      },
    });
  };

  const rationalePopover = useMemo(
    () => (
      <StyledPopover id="ratings-rationale-popover" title="Ratings Rationale">
        <Popover.Body>
          <FormContainer>
            <StyledTextarea
              name="Reason for feedback"
              size="sm"
              as="textarea"
              rows={3}
              disabled={isPositiveRating === undefined}
              placeholder={
                isPositiveRating === undefined
                  ? "Rate this moment first before providing a reason for your rating."
                  : "Thanks for your feedback! What was the reason for your rating?"
              }
              aria-label="Reason for feedback"
              value={
                ratingRationale === null || ratingRationale === undefined
                  ? ""
                  : ratingRationale
              }
              onChange={(e) => {
                setRatingRationale(e.target.value);
              }}
            />
            <ButtonRow>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={isPositiveRating === undefined}
                onClick={handleSaveRationale}
              >
                Submit
              </Button>
            </ButtonRow>
          </FormContainer>
        </Popover.Body>
      </StyledPopover>
    ),
    [isPositiveRating, ratingRationale, handleSaveRationale]
  );

  return (
    <ButtonGroup>
      <Button
        variant="outline-secondary"
        onClick={handleRating(true)}
        aria-label="Thumbs up"
      >
        <i
          data-testid="thumbs-up-icon"
          className={
            isPositiveRating
              ? "bi bi-hand-thumbs-up-fill text-success"
              : "bi bi-hand-thumbs-up text-muted"
          }
          style={{ cursor: "pointer" }}
        />
      </Button>
      <Button
        variant="outline-secondary"
        onClick={handleRating(false)}
        aria-label="Thumbs down"
      >
        <i
          data-testid="thumbs-down-icon"
          className={
            isPositiveRating === false
              ? "bi bi-hand-thumbs-down-fill text-danger"
              : "bi bi-hand-thumbs-down text-muted"
          }
          style={{ cursor: "pointer" }}
        />
      </Button>
      <Button
        ref={rationaleButtonRef}
        variant="outline-secondary"
        onClick={() => setShowPopover(!showPopover)}
        aria-label="Add rationale"
      >
        <i
          className={
            rationale ? "bi bi-chat-left-text-fill" : "bi bi-chat-left-text"
          }
        />
      </Button>
      <Overlay
        show={showPopover}
        target={rationaleButtonRef.current}
        placement="right"
        rootClose
        onHide={() => {
          setShowPopover(false);
          // Update cache when popover closes if there's a pending update
          updateCache();
        }}
      >
        {rationalePopover}
      </Overlay>
    </ButtonGroup>
  );
};

export default LLMEventRating;
